import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth.js";
import { useParams } from "react-router-dom";
import { getUserById } from "../../models/UserFunctions.js"; // Function to fetch user data
import {
  getOrgById,
  getOrgByRef,
  transformGuestsToTable,
  transformSetupSessionsToTable,
  transformUsersToTable,
} from "../../models/OrgFunctions.js"; // Functions to fetch org and client data
import Column from "../../layout/Column.js";
import Row from "../../layout/Row.js";
import Divider from "../../layout/Divider.js";
import Space from "../../layout/Space.js";
import DataGridFull from "../../tables/DataGridFull.js";
import { useTheme } from "@mui/material";
import Icon1 from "../../icons/Icon.js";
import {
  getDocDataById,
  getDocDataByRef,
  getRefFromId,
  queryCollection,
  updateDocDataField,
} from "../../models/DataFunctions.js";
import TFGPT1 from "../../textfields/TFGPT1.js";
import SecondaryButton from "../../buttons/SecondaryButton.js";
import PrimaryButton from "../../buttons/PrimaryButton.js";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi.js";
import { useAppContext } from "../../config/AppContext.js";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import loginAs from "../../api/loginAs.js";

function SetupSessions({ navigateWorkspace }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();

  const [userData, setUserData] = useState(null);
  const [orgData, setOrgData] = useState(null);

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);

  const [selected_users_list, setSelectedUsersList] = useState([]);

  const [sessions, setSessions] = useState([]);
  // Function to navigate to a client by ID
  const gotoUser = (id) => {
    navigateWorkspace(`/setup-session/${id}`);
  };

  // Function to navigate to a client by name

  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = await getUserById(currentUser.uid);

      // Fetch organization data using the user's org reference
      const org = await getOrgById(user.org_id);

      // Fetch clients associated with the user's organization
      const sessions = await queryCollection("setup_session", []);

      // Transform clients into table format (rows and columns)
      const { rows, columns } = transformSetupSessionsToTable(sessions);

      // Update state if the user data is new or changed
      if (user && user !== userData) {
        setUserData(user);
        setOrgData(org);
        setSessions(sessions);
        setRows(rows);
        setColumns(columns);

        let options_cleaned = sessions.map((user) => user.email);
        options_cleaned = options_cleaned.filter((user) => user !== undefined);

        // Set options for the search bar (client names)
        setOptions(
          options_cleaned.map((session) => ({ value: session, label: session }))
        );
      }
    }
  };
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const handleLoginAs = async (id) => {
    const auth = getAuthCentral();

    try {
      if (!id) {
        console.error("Invalid user id");
        return;
      }
      // Sign out the current user before switching
      const token = await currentUser.getIdToken(); // Fetch token from Firebase

      const switch_token = await loginAs(id, token, prefix);

      // Sign in with the custom token
      await signInWithCustomToken(auth, switch_token);

      // Wait until the currentUser is available
      let refreshedUser = null;
      for (let i = 0; i < 10; i++) {
        refreshedUser = auth.currentUser;
        if (refreshedUser) break;
        await new Promise((resolve) => setTimeout(resolve, 200)); // wait 200ms
      }

      if (!refreshedUser) {
        throw new Error("Failed to retrieve the current user after login.");
      }

      // Fetch user data based on currentUser uid
      const userData = await getUserById(refreshedUser.uid);
      if (!userData || !userData.org_id) {
        throw new Error("Invalid user data or missing org_id.");
      }
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  const handleSelectionModelChange = async (selection) => {};

  // Render the client data
  return (
    <Column align="left" className="h-full w-full pl-4">
      <Row align="left" className="w-full h-full">
        <Column
          align="left"
          className="h-full w-full overflow-hidden"
          y={10}
          sx={{ width: "1200px" }}
        >
          <div className="text-hx font-medium">Sessions setup</div>

          <div className="bg-white border-1 border-shadow_25 rounded-full flex flex-row gap-2 items-center px-2">
            <Icon1 icon_name="search" size={20} fill="grey"></Icon1>
            <TFGPT1
              options={options}
              placeholder="Chercher"
              inputValue={searchValue}
              setInputValue={setSearchValue}
              onAction={() => {}}
              className="w-full"
              start_only={false}
            ></TFGPT1>
          </div>

          {/* DataGrid displaying the clients */}

          <div
            style={{ padding: "0px" }}
            className="w-full h-full max-w-c1p2k overflow-y-auto mb-6 pb-6"
          >
            <div className="h-full min-h-6  pb-6">
              <DataGridFull
                rows={rows}
                columns={columns}
                onClick={gotoUser}
                initial_sort_field="date"
                initial_asc="desc"
                handleSelectionModelChange={handleSelectionModelChange}
              />
            </div>
          </div>
        </Column>

        <Divider orientation="vertical" />
      </Row>
    </Column>
  );
}

export default SetupSessions;
